import { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { MainContext } from "../../AppContext";
import {
  hasAssistantAccess,
  resolveAssistantCapabilities,
  type AssistantCapabilities,
} from "../../lib/assistant-access";
import {
  AssistantRequestError,
  deleteConversation,
  getAssistantStatus,
  getConversation,
  isAbortError,
  streamChat,
} from "../../lib/assistant-client";
import {
  ASSISTANT_MAX_MESSAGE_LENGTH,
  applyStreamEvent,
  createAssistantPlaceholder,
  finalizeMessage,
  findRetryTarget,
  messagesFromHistory,
  type AssistantError,
  type ChatMessage,
} from "../../lib/assistant-messages";
import type { AssistantPageContext } from "../../lib/assistant-links";
import {
  ASSISTANT_CONVERSATION_STORAGE_KEY,
  AssistantContext,
  OPEN_ASSISTANT_EVENT,
  type AssistantContextValue,
  type AssistantStatusState,
  type OpenAssistantDetail,
} from "./assistant-context";

const CONVERSATION_ID_PATTERN = /^[0-9a-f-]{8,64}$/i;

function readStoredConversationId(): string | null {
  try {
    const value = localStorage.getItem(ASSISTANT_CONVERSATION_STORAGE_KEY);
    return value && CONVERSATION_ID_PATTERN.test(value) ? value : null;
  } catch {
    return null;
  }
}

function writeStoredConversationId(value: string | null) {
  try {
    if (value) localStorage.setItem(ASSISTANT_CONVERSATION_STORAGE_KEY, value);
    else localStorage.removeItem(ASSISTANT_CONVERSATION_STORAGE_KEY);
  } catch {
    return;
  }
}

let messageCounter = 0;
function nextMessageId(prefix: string): string {
  messageCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${messageCounter}`;
}

export default function AssistantProvider({ children }: { children: ReactNode }) {
  const { token, user } = useContext(MainContext);
  const permissions: number | undefined = user?.permissions;
  const available = !!token && hasAssistantAccess(permissions);

  const [open, setOpenState] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [conversationId, setConversationIdState] = useState<string | null>(() =>
    readStoredConversationId(),
  );
  const [draft, setDraft] = useState("");
  const [draftVersion, setDraftVersion] = useState(0);
  const [enabled, setEnabled] = useState(false);
  const [model, setModel] = useState("");
  const [serverCapabilities, setServerCapabilities] =
    useState<Partial<AssistantCapabilities> | null>(null);
  const [statusState, setStatusState] = useState<AssistantStatusState>("idle");
  const [statusVersion, setStatusVersion] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(false);
  const [historyVersion, setHistoryVersion] = useState(0);

  const abortRef = useRef<AbortController | null>(null);
  const runIdRef = useRef(0);
  const conversationIdRef = useRef<string | null>(conversationId);
  const pendingConversationIdRef = useRef<string | null>(null);
  const lastContextRef = useRef<AssistantPageContext | undefined>(undefined);
  const historyAttemptRef = useRef<string | null>(null);
  const messagesRef = useRef<ChatMessage[]>(messages);
  const previousTokenRef = useRef<string | null | undefined>(undefined);

  messagesRef.current = messages;

  const setConversationId = useCallback((value: string | null) => {
    conversationIdRef.current = value;
    setConversationIdState(value);
    writeStoredConversationId(value);
  }, []);

  const abortActive = useCallback(() => {
    runIdRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
  }, []);

  const resetConversation = useCallback(() => {
    abortActive();
    pendingConversationIdRef.current = null;
    historyAttemptRef.current = null;
    setMessages([]);
    setHistoryLoading(false);
    setHistoryError(false);
    setConversationId(null);
  }, [abortActive, setConversationId]);

  useEffect(() => {
    const previous = previousTokenRef.current;
    previousTokenRef.current = token;
    if (previous !== undefined && previous !== token) {
      resetConversation();
      setOpenState(false);
      setDraft("");
    }
    if (!token) writeStoredConversationId(null);
  }, [token, resetConversation]);

  useEffect(() => {
    setServerCapabilities(null);
    if (!available) {
      setEnabled(false);
      setModel("");
      setStatusState("idle");
      return;
    }
    const controller = new AbortController();
    setStatusState("loading");
    getAssistantStatus(controller.signal)
      .then((status) => {
        if (controller.signal.aborted) return;
        setEnabled(status.enabled);
        setModel(status.model);
        setServerCapabilities(status.capabilities);
        setStatusState("ready");
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setEnabled(false);
        setStatusState(error?.response?.status === 403 ? "forbidden" : "error");
      });
    return () => controller.abort();
  }, [available, token, statusVersion]);

  useEffect(() => {
    if (!open || !available || !conversationId) return;
    if (messagesRef.current.length > 0) return;
    if (historyAttemptRef.current === conversationId) return;
    historyAttemptRef.current = conversationId;

    const controller = new AbortController();
    setHistoryLoading(true);
    setHistoryError(false);
    getConversation(conversationId, controller.signal)
      .then((conversation) => {
        if (controller.signal.aborted) return;
        if (!conversation) {
          setConversationId(null);
          return;
        }
        if (messagesRef.current.length === 0) {
          setMessages(messagesFromHistory(conversation.messages, nextMessageId("h")));
        }
      })
      .catch((error) => {
        if (controller.signal.aborted || isAbortError(error)) return;
        historyAttemptRef.current = null;
        setHistoryError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setHistoryLoading(false);
      });
    return () => {
      controller.abort();
      if (historyAttemptRef.current === conversationId) historyAttemptRef.current = null;
      setHistoryLoading(false);
    };
  }, [open, available, conversationId, historyVersion, setConversationId]);

  const runTurn = useCallback(
    (text: string, context: AssistantPageContext | undefined, replaceFromIndex?: number) => {
      const userMessage: ChatMessage = {
        id: nextMessageId("u"),
        role: "user",
        text,
        tools: [],
        status: "done",
      };
      const assistantId = nextMessageId("a");
      setMessages((previous) => {
        const base =
          replaceFromIndex === undefined ? previous : previous.slice(0, replaceFromIndex);
        return [...base, userMessage, createAssistantPlaceholder(assistantId)];
      });

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      runIdRef.current += 1;
      const runId = runIdRef.current;
      pendingConversationIdRef.current = null;
      lastContextRef.current = context;
      setIsStreaming(true);

      const updateAssistant = (update: (message: ChatMessage) => ChatMessage) => {
        if (runIdRef.current !== runId) return;
        setMessages((previous) =>
          previous.map((message) => (message.id === assistantId ? update(message) : message)),
        );
      };

      streamChat({
        message: text,
        conversationId: conversationIdRef.current,
        context,
        signal: controller.signal,
        onEvent: (event) => {
          if (runIdRef.current !== runId) return;
          if (event.type === "conversation" && event.data.conversationId) {
            pendingConversationIdRef.current = event.data.conversationId;
          }
          if (event.type === "done") {
            const finalId = event.data.conversationId || pendingConversationIdRef.current;
            if (finalId) {
              historyAttemptRef.current = finalId;
              setConversationId(finalId);
            }
          }
          if (event.type === "error" && event.data.code === "not_configured") {
            setEnabled(false);
          }
          updateAssistant((message) => applyStreamEvent(message, event));
        },
      })
        .catch((error) => {
          if (isAbortError(error) || controller.signal.aborted) {
            updateAssistant((message) =>
              message.status === "streaming" ? finalizeMessage(message, "stopped") : message,
            );
            return;
          }
          const failure: AssistantError =
            error instanceof AssistantRequestError
              ? { code: error.code, message: error.message }
              : { code: "internal", message: error?.message || "" };
          if (failure.code === "not_configured") setEnabled(false);
          updateAssistant((message) => finalizeMessage(message, "error", failure));
        })
        .finally(() => {
          if (runIdRef.current !== runId) return;
          updateAssistant((message) =>
            message.status === "streaming"
              ? finalizeMessage(message, "error", {
                  code: "interrupted",
                  message: "",
                })
              : message,
          );
          abortRef.current = null;
          setIsStreaming(false);
        });
    },
    [setConversationId],
  );

  const send = useCallback(
    (text: string, context?: AssistantPageContext) => {
      const trimmed = text.trim();
      if (!trimmed || trimmed.length > ASSISTANT_MAX_MESSAGE_LENGTH) return;
      if (!available || !enabled || abortRef.current || historyLoading) return;
      runTurn(trimmed, context);
    },
    [available, enabled, historyLoading, runTurn],
  );

  const retry = useCallback(() => {
    if (!available || !enabled || abortRef.current) return;
    const target = findRetryTarget(messagesRef.current);
    if (!target) return;
    runTurn(target.text, lastContextRef.current, target.userIndex);
  }, [available, enabled, runTurn]);

  const stop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const newChat = useCallback(() => {
    const previousId = conversationIdRef.current;
    resetConversation();
    if (previousId) deleteConversation(previousId).catch((): void => {});
  }, [resetConversation]);

  const setOpen = useCallback(
    (value: boolean) => {
      setOpenState(value && available);
    },
    [available],
  );

  const toggle = useCallback(() => {
    setOpenState((value) => (available ? !value : false));
  }, [available]);

  useEffect(() => {
    if (!available) setOpenState(false);
  }, [available]);

  useEffect(() => {
    const onKeydown = (event: KeyboardEvent) => {
      if (event.key?.toLowerCase() !== "j") return;
      if (!(event.metaKey || event.ctrlKey) || event.shiftKey || event.altKey) return;
      if (!available) return;
      event.preventDefault();
      setOpenState((value) => !value);
    };
    const onOpen = (event: Event) => {
      if (!available) return;
      const detail = (event as CustomEvent<OpenAssistantDetail>).detail;
      setOpenState(true);
      if (detail?.prompt) {
        setDraft(detail.prompt);
        setDraftVersion((value) => value + 1);
      }
    };
    document.addEventListener("keydown", onKeydown);
    document.addEventListener(OPEN_ASSISTANT_EVENT, onOpen);
    return () => {
      document.removeEventListener("keydown", onKeydown);
      document.removeEventListener(OPEN_ASSISTANT_EVENT, onOpen);
    };
  }, [available]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const capabilities = useMemo(
    () => resolveAssistantCapabilities(serverCapabilities, permissions),
    [serverCapabilities, permissions],
  );

  const refreshStatus = useCallback(() => setStatusVersion((value) => value + 1), []);
  const reloadHistory = useCallback(() => {
    historyAttemptRef.current = null;
    setHistoryError(false);
    setHistoryVersion((value) => value + 1);
  }, []);

  const value = useMemo<AssistantContextValue>(
    () => ({
      available,
      capabilities,
      enabled,
      model,
      statusState,
      refreshStatus,
      open,
      setOpen,
      toggle,
      messages,
      isStreaming,
      historyLoading,
      historyError,
      reloadHistory,
      conversationId,
      draft,
      setDraft,
      draftVersion,
      send,
      stop,
      retry,
      newChat,
    }),
    [
      available,
      capabilities,
      enabled,
      model,
      statusState,
      refreshStatus,
      open,
      setOpen,
      toggle,
      messages,
      isStreaming,
      historyLoading,
      historyError,
      reloadHistory,
      conversationId,
      draft,
      draftVersion,
      send,
      stop,
      retry,
      newChat,
    ],
  );

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}
